/**
 * Cut Order Optimizer
 *
 * Orders cuts for efficient fabrication in a real shop.
 * Instead of cutting pieces in arbitrary order, this optimizer groups cuts
 * that can be made together (e.g., one long rip cut across multiple pieces).
 *
 * Strategy:
 * 1. Group pieces by their Y position (horizontal strips)
 * 2. Within each strip, order by X position (left to right)
 * 3. Identify "through cuts" - cuts that span the full width/height
 * 4. Prioritize through cuts first (they separate the sheet into manageable sections)
 * 5. Then do individual piece cuts within each section
 */

/**
 * Tolerance for grouping cuts at similar positions (in inches)
 */
const POSITION_TOLERANCE = 0.25;

/**
 * Check if two values are approximately equal within tolerance
 */
const approxEqual = (a, b, tolerance = POSITION_TOLERANCE) => {
  return Math.abs(a - b) <= tolerance;
};

/**
 * Find horizontal through-cuts (cuts that go all the way across the sheet width)
 * These are cuts where multiple pieces share the same Y edge
 */
const findHorizontalThroughCuts = (pieces, sheetWidth, cutWidth) => {
  const throughCuts = [];

  // Collect all unique Y positions (top and bottom edges of pieces)
  const yEdges = new Map(); // y-position -> array of pieces that have an edge there

  pieces.forEach(piece => {
    const topY = piece.y;
    const bottomY = piece.y + piece.width;

    // Track top edges
    if (!yEdges.has(topY)) yEdges.set(topY, { top: [], bottom: [] });
    yEdges.get(topY).top.push(piece);

    // Track bottom edges
    if (!yEdges.has(bottomY)) yEdges.set(bottomY, { top: [], bottom: [] });
    yEdges.get(bottomY).bottom.push(piece);
  });

  // Find Y positions where edges span most of the sheet width
  yEdges.forEach((edgePieces, yPos) => {
    // Skip edges at y=0 (sheet edge, no cut needed)
    if (yPos < cutWidth) return;

    // Calculate total coverage at this Y level
    const allPieces = [...edgePieces.top, ...edgePieces.bottom];
    if (allPieces.length < 2) return;

    // Sort by X position and check coverage
    const sortedByX = allPieces.sort((a, b) => a.x - b.x);
    let coverage = 0;
    let lastX = 0;

    sortedByX.forEach(piece => {
      if (piece.x <= lastX + cutWidth) {
        coverage = Math.max(coverage, piece.x + piece.length);
        lastX = piece.x + piece.length + cutWidth;
      }
    });

    // If coverage is at least 80% of sheet width, it's a good through-cut candidate
    if (coverage >= sheetWidth * 0.8) {
      throughCuts.push({
        type: 'horizontal',
        position: yPos,
        pieceCount: allPieces.length
      });
    }
  });

  return throughCuts.sort((a, b) => a.position - b.position);
};

/**
 * Find vertical through-cuts (cuts that go all the way down the sheet height)
 */
const findVerticalThroughCuts = (pieces, sheetWidth, cutWidth) => {
  const throughCuts = [];

  // Collect all unique X positions (left and right edges of pieces)
  const xEdges = new Map();

  pieces.forEach(piece => {
    const leftX = piece.x;
    const rightX = piece.x + piece.length;

    if (!xEdges.has(leftX)) xEdges.set(leftX, { left: [], right: [] });
    xEdges.get(leftX).left.push(piece);

    if (!xEdges.has(rightX)) xEdges.set(rightX, { left: [], right: [] });
    xEdges.get(rightX).right.push(piece);
  });

  xEdges.forEach((edgePieces, xPos) => {
    if (xPos < cutWidth) return;

    const allPieces = [...edgePieces.left, ...edgePieces.right];
    if (allPieces.length < 2) return;

    const sortedByY = allPieces.sort((a, b) => a.y - b.y);
    let coverage = 0;
    let lastY = 0;

    sortedByY.forEach(piece => {
      if (piece.y <= lastY + cutWidth) {
        coverage = Math.max(coverage, piece.y + piece.width);
        lastY = piece.y + piece.width + cutWidth;
      }
    });

    if (coverage >= sheetWidth * 0.8) {
      throughCuts.push({
        type: 'vertical',
        position: xPos,
        pieceCount: allPieces.length
      });
    }
  });

  return throughCuts.sort((a, b) => a.position - b.position);
};

/**
 * Group pieces into horizontal strips based on Y position
 */
const groupIntoStrips = (pieces, cutWidth) => {
  if (pieces.length === 0) return [];

  // Sort pieces by Y position
  const sortedPieces = [...pieces].sort((a, b) => a.y - b.y);

  const strips = [];
  let currentStrip = null;

  sortedPieces.forEach(piece => {
    // Check if this piece belongs to current strip (overlaps in Y)
    if (currentStrip) {
      const stripBottom = Math.max(...currentStrip.pieces.map(p => p.y + p.width));
      const pieceTop = piece.y;

      // If piece overlaps with current strip (with some tolerance for kerf)
      if (pieceTop < stripBottom + cutWidth * 2) {
        currentStrip.pieces.push(piece);
        currentStrip.maxY = Math.max(currentStrip.maxY, piece.y + piece.width);
        return;
      }
    }

    // Start a new strip
    currentStrip = {
      minY: piece.y,
      maxY: piece.y + piece.width,
      pieces: [piece]
    };
    strips.push(currentStrip);
  });

  // Sort pieces within each strip by X position (left to right)
  strips.forEach(strip => {
    strip.pieces.sort((a, b) => a.x - b.x);
  });

  return strips;
};

/**
 * Create an ordered cut sequence for a single sheet layout
 *
 * @param {Object} layout - Sheet layout with pieces
 * @param {number} cutWidth - Blade kerf width
 * @returns {Array} Ordered array of cuts with metadata
 */
export const createCutSequence = (layout, cutWidth = 0.125) => {
  const { pieces, width: sheetW, length: sheetL } = layout;
  if (!pieces || pieces.length === 0) return [];

  const cutSequence = [];
  let cutNumber = 1;

  // Step 1: Find major through-cuts
  const horizontalThroughCuts = findHorizontalThroughCuts(pieces, sheetL, cutWidth);
  const verticalThroughCuts = findVerticalThroughCuts(pieces, sheetW, cutWidth);

  // Step 2: Add through-cuts first (they're most efficient - one cut separates multiple pieces)
  // Prioritize the cuts that affect the most pieces
  const allThroughCuts = [
    ...horizontalThroughCuts.map(c => ({ ...c, priority: c.pieceCount })),
    ...verticalThroughCuts.map(c => ({ ...c, priority: c.pieceCount }))
  ].sort((a, b) => b.priority - a.priority);

  allThroughCuts.slice(0, 3).forEach(throughCut => {
    cutSequence.push({
      cutNumber: cutNumber++,
      type: 'through-cut',
      direction: throughCut.type,
      position: throughCut.position,
      description: throughCut.type === 'horizontal'
        ? `Horizontal rip at Y=${throughCut.position.toFixed(2)}" (separates ${throughCut.pieceCount} pieces)`
        : `Vertical crosscut at X=${throughCut.position.toFixed(2)}" (separates ${throughCut.pieceCount} pieces)`,
      affectedPieces: throughCut.pieceCount
    });
  });

  // Step 3: Group remaining pieces into strips and order for cutting
  const strips = groupIntoStrips(pieces, cutWidth);

  // Step 4: For each strip, add individual piece cuts
  strips.forEach((strip, stripIndex) => {
    // Add strip separator note if multiple strips
    if (strips.length > 1 && strip.pieces.length > 0) {
      cutSequence.push({
        cutNumber: cutNumber++,
        type: 'strip-start',
        stripIndex: stripIndex + 1,
        yRange: `Y: ${strip.minY.toFixed(1)}" - ${strip.maxY.toFixed(1)}"`,
        description: `Strip ${stripIndex + 1}: ${strip.pieces.length} pieces`,
        pieces: strip.pieces.map(p => p.name || p.id)
      });
    }

    // Add pieces in order (left to right within strip)
    strip.pieces.forEach(piece => {
      cutSequence.push({
        cutNumber: cutNumber++,
        type: 'piece',
        piece: piece,
        description: `Cut: ${piece.name || 'Piece'} (${piece.length.toFixed(2)}" × ${piece.width.toFixed(2)}")`,
        position: { x: piece.x, y: piece.y },
        dimensions: { width: piece.width, length: piece.length },
        rotated: piece.rotated,
        grainDirection: piece.grainDirection
      });
    });
  });

  return cutSequence;
};

/**
 * Order all cuts across all layouts for efficient fabrication
 *
 * @param {Array} layouts - Array of sheet layouts
 * @param {number} cutWidth - Blade kerf width
 * @returns {Array} Array of ordered layouts with cut sequences
 */
export const orderCutsForFabrication = (layouts, cutWidth = 0.125) => {
  return layouts.map((layout, index) => ({
    ...layout,
    sheetNumber: index + 1,
    cutSequence: createCutSequence(layout, cutWidth)
  }));
};

/**
 * Get a flat list of all pieces in optimized cut order
 *
 * @param {Array} layouts - Array of sheet layouts
 * @param {number} cutWidth - Blade kerf width
 * @returns {Array} Flat array of pieces in cut order
 */
export const getOrderedPieceList = (layouts, cutWidth = 0.125) => {
  const orderedLayouts = orderCutsForFabrication(layouts, cutWidth);
  const allPieces = [];

  orderedLayouts.forEach(layout => {
    layout.cutSequence
      .filter(cut => cut.type === 'piece')
      .forEach(cut => {
        allPieces.push({
          ...cut.piece,
          sheetNumber: layout.sheetNumber,
          cutNumber: cut.cutNumber,
          thicknessLabel: layout.thicknessLabel
        });
      });
  });

  return allPieces;
};

export default {
  orderCutsForFabrication,
  createCutSequence,
  getOrderedPieceList
};
